// client/src/components/pages/LexiStar/Drivers/ImportDialog.jsx
//
// Bulk driver import (FR-DRV-002 bulk import; POST /drivers:import). The file
// is checked first with a dry run — the server's own validation, row by row —
// then imported. Rows whose tag matches an existing driver update it. An
// `opt_in` column records consents, which need the wording the drivers agreed
// to (FR-NTF-011). "Skip invalid rows" imports the good rows only.

import React, { useEffect, useState } from 'react';
import { FaTimes, FaFileCsv, FaDownload } from 'react-icons/fa';
import { inputClasses } from '../StatusBoard/atoms';
import { importDrivers } from './driversApi';
import { errorText } from './DriverDialog';
import { CSV_TEMPLATE, defaultConsentWording } from './driverLogic';

const ACTION_META = {
  create: { label: 'New', className: 'bg-emerald-50 text-emerald-800' },
  update: { label: 'Update', className: 'bg-sky-50 text-sky-800' },
  error: { label: 'Error', className: 'bg-red-50 text-red-700' },
};

const hasOptInColumn = (csv) =>
  /(^|[,;])\s*"?(opt_in|opted_in|consent|opt_in_channels)"?\s*([,;]|$)/i.test(
    String(csv || '').split(/\r?\n/, 1)[0] || ''
  );

function Report({ report }) {
  if (!report) return null;
  const rows = report.rows || [];
  return (
    <div className="space-y-2" aria-label="Import report">
      {report.summary && <p className="text-sm text-slate-800">{report.summary}</p>}
      {rows.length > 0 && (
        <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-slate-500">
                <th className="px-2 py-1.5 font-medium">Line</th>
                <th className="px-2 py-1.5 font-medium">Result</th>
                <th className="px-2 py-1.5 font-medium">Name</th>
                <th className="px-2 py-1.5 font-medium">Tag</th>
                <th className="px-2 py-1.5 font-medium">Problems</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const meta = ACTION_META[r.action] || ACTION_META.error;
                return (
                  <tr key={r.line} className="border-t border-slate-100">
                    <td className="px-2 py-1 tabular-nums">{r.line}</td>
                    <td className="px-2 py-1">
                      <span className={`px-1.5 py-0.5 rounded font-semibold ${meta.className}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-2 py-1">{r.name || '—'}</td>
                    <td className="px-2 py-1">{r.tag || '—'}</td>
                    <td className="px-2 py-1 text-red-700">{(r.errors || []).join('; ')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const summaryOf = (res, dry) =>
  `${dry ? 'Check: ' : 'Imported: '}${res.created ?? 0} new, ${res.updated ?? 0} updated` +
  `${res.consents ? `, ${res.consents} opt-in${res.consents === 1 ? '' : 's'} recorded` : ''}` +
  `${res.failed ? `, ${res.failed} row${res.failed === 1 ? '' : 's'} with problems` : ''}.`;

export default function ImportDialog({ isOpen, onClose, onImported }) {
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [wording, setWording] = useState(defaultConsentWording('whatsapp'));
  const [partial, setPartial] = useState(false);
  const [report, setReport] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCsv('');
      setFileName('');
      setReport(null);
      setChecked(false);
      setError(null);
      setDone(false);
      setPartial(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const optIns = hasOptInColumn(csv);
  const options = (dry) => ({
    ...(dry ? { dry_run: true } : {}),
    ...(partial ? { partial: true } : {}),
    ...(optIns && wording.trim() ? { consent_wording: wording.trim() } : {}),
  });

  const run = async (dry) => {
    setBusy(true);
    setError(null);
    try {
      const res = await importDrivers(csv, options(dry));
      setReport({ summary: summaryOf(res, dry), rows: res.rows || [] });
      if (dry) setChecked(true);
      else {
        setDone(true);
        onImported?.(res);
      }
    } catch (err) {
      const data = err.response?.data || {};
      setReport(data.rows ? { summary: null, rows: data.rows } : null);
      setError(errorText(err, dry ? 'The file could not be checked' : 'The import failed'));
      setChecked(dry && Array.isArray(data.rows));
    } finally {
      setBusy(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setChecked(false);
    setReport(null);
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ''));
    reader.readAsText(file);
  };

  const templateHref = `data:text/csv;charset=utf-8,${encodeURIComponent(CSV_TEMPLATE)}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Import drivers"
        className="bg-surface rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[92vh] flex flex-col overflow-hidden"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Import drivers from CSV</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600"
          >
            <FaTimes size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <p className="text-sm text-slate-600">
            Columns:{' '}
            <span className="font-mono text-xs">
              name, tag, language, phone, telegram_chat_id, preferred_channel, active, notes, opt_in
            </span>
            . Only <em>name</em> is required. A row with an existing tag updates that driver.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-300 rounded-lg hover:bg-slate-50 cursor-pointer">
              <FaFileCsv className="text-slate-500" />
              {fileName || 'Choose a CSV file'}
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={onFile}
                className="sr-only"
                aria-label="CSV file"
              />
            </label>
            <a
              href={templateHref}
              download="lexistar-drivers-template.csv"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline"
            >
              <FaDownload size={11} /> Template
            </a>
          </div>
          <div>
            <label htmlFor="import-csv" className="block text-xs font-medium text-slate-600 mb-1">
              …or paste the CSV
            </label>
            <textarea
              id="import-csv"
              value={csv}
              onChange={(e) => {
                setCsv(e.target.value);
                setChecked(false);
                setReport(null);
              }}
              rows={5}
              className={`${inputClasses} font-mono text-xs`}
              placeholder={CSV_TEMPLATE}
            />
          </div>
          {optIns && (
            <div>
              <label
                htmlFor="import-wording"
                className="block text-xs font-medium text-slate-600 mb-1"
              >
                Opt-in wording the drivers agreed to (stored with each consent)
              </label>
              <textarea
                id="import-wording"
                value={wording}
                onChange={(e) => setWording(e.target.value)}
                rows={2}
                className={inputClasses}
              />
            </div>
          )}
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={partial}
              onChange={(e) => {
                setPartial(e.target.checked);
                setChecked(false);
              }}
              className="rounded border-slate-300"
            />
            Skip invalid rows (import the rest)
          </label>
          {error && (
            <p
              role="alert"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {error}
            </p>
          )}
          <Report report={report} />
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <>
              <button
                onClick={() => run(true)}
                disabled={!csv.trim() || busy || (optIns && !wording.trim())}
                className="px-4 py-2 text-sm font-semibold text-primary-700 bg-surface border border-primary-400 rounded-lg hover:bg-primary-50 disabled:opacity-50"
              >
                {busy && !checked ? 'Checking...' : 'Check file'}
              </button>
              <button
                onClick={() => run(false)}
                disabled={!checked || busy}
                title={checked ? undefined : 'Check the file first'}
                className="px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm bg-primary-500 hover:bg-primary-600 disabled:bg-slate-400 disabled:cursor-not-allowed"
              >
                {busy && checked ? 'Importing...' : 'Import'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
